import React, { useEffect, useState } from 'react';
import FooterBlue from '../../components/FooterBlue';
import { Link } from 'react-router-dom';
import api from "../../utils/axiosInstance";
import Select from 'react-select';

const DriverDashboard = () => {
    const [driver, setDriver] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [carriers, setCarriers] = useState([]);
    const [selectedResumeFile, setSelectedResumeFile] = useState(null);
    const [messageModal, setMessageModal] = useState({
        show: false,
        title: '',
        message: '',
        isSuccess: true
    });
    const [latestResume, setLatestResume] = useState(null);

    const trailerOptions = [
        { value: "Van", label: "Van" },
        { value: "Flatbed", label: "Flatbed" },
        { value: "Reefer", label: "Reefer" },
        { value: "Tanker", label: "Tanker" },
        { value: "Other", label: "Others" },
    ];

    const routeOptions = [
        { value: "OTR", label: "Over the Road" },
        { value: "Regional", label: "Regional" },
        { value: "Local", label: "Local" },
        { value: "All", label: "All Routes" },
    ];

    const endorsementOptions = [
        { value: "Hazmat", label: "Hazmat" },
        { value: "Double", label: "Doubles/Triples" },
        { value: "Tank", label: "Tank" },
        { value: "Hazardous", label: "Hazardous Tanker" },
        { value: "Passenger", label: "Passenger" },
        { value: "None", label: "None of these" },
    ];

    useEffect(() => {
        const stored = localStorage.getItem('driver');
        if (stored) {
            try {
                const driverData = JSON.parse(stored);
                setDriver(driverData);
                setEditForm(driverData);
            } catch (err) {
                console.error("Failed to parse driver data:", err);
                localStorage.removeItem('driver');
            }
        }
    }, []);

    useEffect(() => {
        const fetchLatestResume = async () => {
            if (driver?._id) {
                try {
                    const res = await api.get(`/resumes/latest/${driver._id}`);
                    if (res.data.success) {
                        setLatestResume(res.data.resume);
                    }
                } catch (err) {
                    console.error('Error fetching resume:', err.message);
                }
            }
        };
        fetchLatestResume();
    }, [driver]);

    const fetchInterestedCarriers = async () => {
        try {
            const res = await api.get(`/interests/driver/${driver._id}`);
            setCarriers(res.data || []);
        } catch (error) {
            console.error('Failed to fetch carriers:', error.message);
            setCarriers([]);
        }
    };

    // 🟢 EDIT: Save to new "driverEdits" endpoint, no forced resume upload
    const handleEditSubmit = async () => {
        try {
            const formData = new FormData();

            // Always include driver id for reference
            formData.append('driverId', driver._id);

            // Append all the editForm non-file fields
            for (let key in editForm) {
                const value = editForm[key];
                if (Array.isArray(value)) {
                    value.forEach(val => formData.append(key, val));
                } else {
                    formData.append(key, value);
                }
            }

            // Resume upload is optional
            if (selectedResumeFile) {
                formData.append('resume', selectedResumeFile);
            }

            // POST to new driverEdits collection
            const res = await api.post(
                '/driverEdits',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (res.data.success) {
                setMessageModal({
                    show: true,
                    title: 'Success',
                    message: 'Your profile update has been submitted for admin review.',
                    isSuccess: true
                });
                setEditModal(false);
            } else {
                throw new Error(res.data.message || 'Unknown server error');
            }
        } catch (err) {
            setMessageModal({
                show: true,
                title: 'Update Failed',
                message: err.response?.data?.message || err.message || 'Something went wrong.',
                isSuccess: false
            });
        }
    };

    if (!driver) return <div className="text-center py-20">Loading driver data...</div>;

    return (
        <div className="bg-gradient-to-br from-accent to-gray-200 text-primary min-h-screen flex flex-col">
            {/* NAVBAR */}
            <nav className="bg-[#292626] px-6 py-5 shadow-lg">
                <div className="flex items-center justify-between">
                    <a href="/">
                        <img src="../assets/FleetyBlue.png" alt="Fleety Logo" className="h-12" />
                    </a>
                    <Link
                        to="/"
                        className="bg-primary text-white border-none px-5 py-2 rounded-full font-semibold flex items-center gap-2 hover:bg-primary-dark hover:-translate-y-0.5 transition"
                    >
                        <i className="fas fa-sign-out-alt"></i> Logout
                    </Link>
                </div>
            </nav>

            <main className="flex-grow p-8 max-w-6xl mx-auto w-full">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-black">Driver Dashboard</h1>
                    <button
                        onClick={() => setEditModal(true)}
                        className="bg-black hover:bg-blue-700 text-white px-4 py-2 rounded-full shadow"
                    >
                        <i className="fas fa-edit mr-2"></i>Edit Profile
                    </button>
                </div>

                {/* INFO CARD */}
                <div className="bg-white rounded-2xl shadow-xl">
                    <div className="bg-gradient-to-r from-primary to-[#2a2a2a] p-8 text-white">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between">
                            <div>
                                <h2 className="text-xl font-bold">{driver.name || 'Driver'}</h2>
                                <span className="bg-blue-500 text-white px-2 py-1 text-sm font-semibold rounded-full mt-2 inline-block">Professional Driver</span>
                            </div>
                            <button
                                onClick={() => { fetchInterestedCarriers(); setShowModal(true); }}
                                className="bg-white text-black font-semibold px-4 py-2 rounded-full shadow hover:bg-gray-200 transition mt-4 md:mt-0"
                            >
                                My Carriers
                            </button>
                        </div>
                    </div>

                    {/* DRIVER DETAILS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-8">
                        {[{ icon: 'fa-road', label: 'Route Type', value: Array.isArray(driver.route) ? driver.route.join(', ') : driver.route || 'N/A' },
                        { icon: 'fa-briefcase', label: 'Job Type', value: driver.job || 'N/A' },
                        { icon: 'fa-truck-moving', label: 'Trailer Type', value: Array.isArray(driver.trailer) ? driver.trailer.join(', ') : driver.trailer || 'N/A' },
                        { icon: 'fa-id-card', label: 'CDL Type', value: driver.cdl || 'N/A' },
                        { icon: 'fa-certificate', label: 'Endorsements', value: Array.isArray(driver.endorsements) ? driver.endorsements.join(', ') : driver.endorsements || 'None' },
                        { icon: 'fa-phone', label: 'Phone', value: driver.phone || 'N/A' },
                        { icon: 'fa-map-marker-alt', label: 'Location', value: `${driver.city || 'Unknown'}, ${driver.state || ''}` },
                        { icon: 'fa-home', label: 'Address', value: `${driver.address || 'N/A'}, ${driver.zip || ''}` },
                        { icon: 'fa-user-clock', label: 'Experience', value: driver.experience || 'N/A' }].map((item, index) => (
                            <div key={index} className="flex items-start gap-4 p-4 bg-gray-100 rounded-xl hover:bg-gray-200 transition">
                                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                    <i className={`fas ${item.icon} text-blue-600 text-xl`}></i>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-700">{item.label}</h3>
                                    <p className="text-black">{item.value}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CARRIERS MODAL */}
                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8">
                            <div className="flex justify-between mb-4">
                                <h2 className="text-2xl font-bold text-black">Interested Carriers</h2>
                                <button onClick={() => setShowModal(false)} className="text-gray-600 hover:text-blue-600 text-3xl font-bold">&times;</button>
                            </div>
                            <ul className="space-y-3 max-h-64 overflow-y-auto">
                                {carriers.length > 0 ? (
                                    carriers.map((c, i) => <li key={i} className="p-3 bg-gray-50 rounded-lg shadow">{c.companyName}</li>)
                                ) : (
                                    <li className="text-gray-500">No carriers found.</li>
                                )}
                            </ul>
                        </div>
                    </div>
                )}

                {/* EDIT PROFILE MODAL */}
                {editModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto">
                            <h2 className="text-2xl font-bold mb-4 text-black">Edit Profile</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {["name", "email", "phone", "job", "cdl", "city", "state", "address", "zip", "experience"].map((field) => (
                                    <input
                                        key={field}
                                        type="text"
                                        placeholder={field}
                                        value={editForm[field] || ''}
                                        onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })}
                                        className="border border-gray-400 rounded px-4 py-2"
                                    />
                                ))}

                                {/* Route Type */}
                                <div>
                                    <label className="block mb-1 font-medium text-sm text-gray-700">Route Type</label>
                                    <Select
                                        isMulti
                                        options={routeOptions}
                                        value={routeOptions.filter(opt => (editForm.route || []).includes(opt.value))}
                                        onChange={(selected) =>
                                            setEditForm({ ...editForm, route: selected.map(opt => opt.value) })
                                        }
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                {/* Trailer Type */}
                                <div>
                                    <label className="block mb-1 font-medium text-sm text-gray-700">Trailer Type</label>
                                    <Select
                                        isMulti
                                        options={trailerOptions}
                                        value={trailerOptions.filter(opt => (editForm.trailer || []).includes(opt.value))}
                                        onChange={(selected) =>
                                            setEditForm({ ...editForm, trailer: selected.map(opt => opt.value) })
                                        }
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                {/* Endorsements */}
                                <div>
                                    <label className="block mb-1 font-medium text-sm text-gray-700">Endorsements</label>
                                    <Select
                                        isMulti
                                        options={endorsementOptions}
                                        value={endorsementOptions.filter(opt => (editForm.endorsements || []).includes(opt.value))}
                                        onChange={(selected) =>
                                            setEditForm({ ...editForm, endorsements: selected.map(opt => opt.value) })
                                        }
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </div>
                            </div>

                            {/* Resume Upload (optional) */}
                            <div className="mt-4">
                                <label className="block text-sm font-medium mb-2">Upload Resume (optional)</label>
                                <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setSelectedResumeFile(e.target.files[0])} className="border px-4 py-2 rounded w-full" />
                            </div>

                            {/* Buttons */}
                            <div className="mt-6 flex justify-end gap-4">
                                <button onClick={() => setEditModal(false)} className="bg-gray-300 px-4 py-2 rounded">Cancel</button>
                                <button onClick={handleEditSubmit} className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-black-700">Save</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MESSAGE MODAL */}
                {messageModal.show && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
                            <h2 className={`text-2xl font-bold mb-4 ${messageModal.isSuccess ? 'text-blue-600' : 'text-black'}`}>
                                {messageModal.title}
                            </h2>
                            <p className="text-gray-700 mb-6">{messageModal.message}</p>
                            <button
                                onClick={() => setMessageModal({ ...messageModal, show: false })}
                                className="bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </main>
            <FooterBlue />
        </div>
    );
};

export default DriverDashboard;
